import { Box, Popover, Stack } from '@mui/material';
import React, {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import CloseSVG from '@sbu/assets.svg/src/close.svg';
import { Chip } from '@sbu/components.chip';
import { TagFields } from '@sbu/hooks.tags';
import { IconButton } from '@sumologic/yama-ds-components';

import { Field, FilterFunctionType, FilterTypes } from '../types';
import { FilterChip } from './FilterChip';

export interface FilterChipsProps {
  fields: Field[];
  filters: FilterTypes[];
  onEditField: FilterFunctionType;
  onEditOperator: FilterFunctionType;
  onEditValue: FilterFunctionType;
  removeFilter: FilterFunctionType;
  deserializeKeyValue?: (value: string) => TagFields;
  clearFilters: (event: React.MouseEvent) => void;
}

export const FilterChips: FC<React.PropsWithChildren<FilterChipsProps>> = ({
  fields,
  filters,
  onEditField,
  onEditOperator,
  onEditValue,
  removeFilter,
  deserializeKeyValue,
  clearFilters,
}) => {
  const [additionalFiltersAnchorEl, setAdditionalFiltersAnchorEl] =
    useState<HTMLElement | null>(null);
  const menuOpen = Boolean(additionalFiltersAnchorEl);

  const containerRef = useRef<HTMLDivElement>(null);
  const [overflowFilters, setOverflowFilters] = useState<FilterTypes[]>([]);

  const updateOverflowFilters = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    if (filters.length === 0) {
      setOverflowFilters([]);
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();

    let visibleCount = 0;

    const chipElements = Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(
        '[data-sumo-test="filter-chip"]'
      )
    );
    chipElements.forEach((chip) => {
      const chipRect = chip.getBoundingClientRect();
      const isVisibleWithinContainer = chipRect.right <= containerRect.right;
      if (isVisibleWithinContainer) {
        visibleCount++;
      }
    });

    setOverflowFilters(filters.slice(visibleCount));
  }, [filters]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateOverflowFilters();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [filters, updateOverflowFilters]);

  const handleFilterInteraction = (e: React.MouseEvent<HTMLElement>) => {
    // stop the main filter from being opened
    e.stopPropagation();
    setAdditionalFiltersAnchorEl(null);
  };

  const handleOpenAdditionalFilters = (e: React.MouseEvent<HTMLElement>) => {
    // stop the main filter from being opened
    e.stopPropagation();
    setAdditionalFiltersAnchorEl(menuOpen ? null : e.currentTarget);
  };

  const handleClearFilters = (e: React.MouseEvent<HTMLElement>) => {
    handleFilterInteraction(e);
    setOverflowFilters([]);
    clearFilters(e);
  };

  const handleFilterChipClick = (
    e: React.MouseEvent<HTMLElement>,
    filter: FilterTypes,
    callback: (filter: FilterTypes) => void
  ) => {
    handleFilterInteraction(e);
    callback(filter);
  };

  const renderFilterChip = (filter: FilterTypes) => (
    <FilterChip
      fields={fields}
      filter={filter}
      onEditField={(e: React.MouseEvent<HTMLElement>, filter: FilterTypes) =>
        handleFilterChipClick(e, filter, onEditField)
      }
      onEditOperator={(e: React.MouseEvent<HTMLElement>, filter: FilterTypes) =>
        handleFilterChipClick(e, filter, onEditOperator)
      }
      onEditValue={(e: React.MouseEvent<HTMLElement>, filter: FilterTypes) =>
        handleFilterChipClick(e, filter, onEditValue)
      }
      removeFilter={(e: React.MouseEvent<HTMLElement>, filter: FilterTypes) =>
        handleFilterChipClick(e, filter, removeFilter)
      }
      deserializeKeyValue={deserializeKeyValue}
    />
  );

  return (
    <>
      <Stack
        direction='row'
        ref={containerRef}
        sx={{
          maxWidth: 'calc(100% - 400px)',
          overflowY: 'hidden',
          overflowX: 'hidden',
          whiteSpace: 'nowrap',
          position: 'absolute',
          right: '0',
          marginRight: '38px',
        }}
      >
        {filters.map((filter: FilterTypes, index: number) => (
          <Fragment key={`filter-chip-${index}`}>
            {renderFilterChip(filter)}
          </Fragment>
        ))}
      </Stack>
      {overflowFilters.length > 0 && (
        <>
          <Box
            sx={{
              backgroundColor: 'background.paper',
              minHeight: '26px',
              paddingLeft: 1.25,
              position: 'absolute',
              right: 38,
              zIndex: 1,
            }}
          >
            <Chip
              label={`+${overflowFilters.length}`}
              onLabelClick={handleOpenAdditionalFilters}
              variant='outlined'
            />
          </Box>
          <Popover
            anchorEl={additionalFiltersAnchorEl}
            anchorOrigin={{
              horizontal: 'left',
              vertical: 'top',
            }}
            transformOrigin={{
              horizontal: 'left',
              vertical: 'top',
            }}
            open={menuOpen}
            onClose={handleFilterInteraction}
          >
            <Stack
              data-sumo-test='overflow-filters'
              direction='row'
              flexWrap='wrap'
              rowGap={1.75}
              pl={2}
              pr={6}
              pt={2}
              pb={2}
              maxWidth='80vw'
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton
                size='medium'
                aria-label='Clear all filters'
                onClick={handleClearFilters}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: 16,
                  zIndex: 10000,
                  p: 0,
                }}
              >
                <CloseSVG />
              </IconButton>
              {overflowFilters.map((filter: any, index: number) => (
                <Fragment key={`additional-filter-chip-${index}`}>
                  {renderFilterChip(filter)}
                </Fragment>
              ))}
            </Stack>
          </Popover>
        </>
      )}
    </>
  );
};
